import { Box, Text, HStack, VStack } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import type { DailyTurfCoverageDto, DailyCanvasserStatsDto } from '../../../types/campaign'
import { assignCanvasserColors } from '../../../utils/canvasserColors'

interface Props {
  turfCoverage: DailyTurfCoverageDto[]
  canvasserStats: DailyCanvasserStatsDto[]
}

export function TurfCoverageList({ turfCoverage, canvasserStats }: Props) {
  if (turfCoverage.length === 0) {
    return (
      <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
        <Text color="gray.500" fontSize="sm">No turf assignments for this date</Text>
      </Box>
    )
  }

  // Build canvasser -> colour map by identity, ranked the same way as the pie
  // chart (contribution desc). Only the top CANVASSER_TOP_N get an individual
  // viz.cat colour; everyone else falls back to the shared muted Other colour.
  const sorted = [...canvasserStats].sort(
    (a, b) => (b.doorsKnocked + b.businessesVisited) - (a.doorsKnocked + a.businessesVisited),
  )
  const slots = assignCanvasserColors(sorted)
  const canvasserColorMap = new Map<string, string>()
  slots.forEach((slot) => {
    if (!slot.isOther) canvasserColorMap.set(slot.item.grantId, slot.colorVar)
  })
  const colorForCanvasser = (grantId: string): string =>
    canvasserColorMap.get(grantId) ?? 'var(--chakra-colors-viz-party-other)'

  return (
    <VStack gap={3} align="stretch">
      {turfCoverage.map((turf) => {
        const pct = turf.totalMembers > 0
          ? (turf.visitedMembers / turf.totalMembers) * 100
          : 0
        const totalVisits = turf.canvasserContributions.reduce((sum, c) => sum + c.visitCount, 0)

        return (
          <Box key={turf.turfId}>
            <HStack justify="space-between" mb={1}>
              <HStack gap={2}>
                <Text fontSize="sm" fontWeight="medium">
                  {turf.turfLabel}
                </Text>
                {turf.canvasserContributions.length === 1 && (
                  <Text fontSize="xs" color="gray.500">
                    ({turf.canvasserContributions[0].fullName})
                  </Text>
                )}
                {turf.canvasserContributions.length > 1 && (
                  <Text fontSize="xs" color="gray.500">
                    ({turf.canvasserContributions.length} canvassers)
                  </Text>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.500">
                {turf.visitedMembers}/{turf.totalMembers}
                {turf.visitedToday > 0 && (
                  <Text as="span" color="green.500" ml={1}>
                    +{turf.visitedToday} today
                  </Text>
                )}
                {turf.pendingCallbacks > 0 && (
                  <Text as="span" color="orange.500" ml={1}>
                    • {turf.pendingCallbacks} to retry
                  </Text>
                )}
              </Text>
            </HStack>

            {/* Segmented progress bar */}
            <Box
              h="8px"
              bg="gray.100"
              borderRadius="full"
              overflow="hidden"
              position="relative"
            >
              {turf.totalMembers > 0 && (
                <HStack
                  gap={0}
                  h="100%"
                  w={`${Math.min(pct, 100)}%`}
                  position="absolute"
                  top={0}
                  left={0}
                >
                  {turf.canvasserContributions.map((contrib) => {
                    const segmentPct = totalVisits > 0
                      ? (contrib.visitCount / totalVisits) * 100
                      : 0
                    return (
                      <Tooltip contentProps={{ fontSize: "xs" }}
                        key={contrib.grantId}
                        content={`${contrib.fullName}: ${contrib.visitCount} visits`}
                        showArrow
                      >
                        <Box
                          h="100%"
                          flex={segmentPct}
                          bg={colorForCanvasser(contrib.grantId)}
                          transition="flex 0.3s"
                        />
                      </Tooltip>
                    );
                  })}
                  {/* If visited members exist but no contributions tracked, show a default bar */}
                  {turf.canvasserContributions.length === 0 && turf.visitedMembers > 0 && (
                    <Box h="100%" flex={1} bg={pct === 100 ? 'green.400' : 'accent'} />
                  )}
                </HStack>
              )}
            </Box>
          </Box>
        );
      })}
    </VStack>
  );
}
